import { useEffect, useRef, useState } from 'react';
import {
  List,
  ListItem,
  Button,
  Grid,
  TextField,
  Typography,
  Tabs,
  Tab,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Alert
} from '@mui/material';
import { API_BASE_URL } from 'src/apiConfig';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { cyrillicToLatin } from 'src/helper/helperFunction';
import AddStudentDialog from 'src/components/dialogs/student/addStudentDialog';

const FinalConnectWorkPage = () => {
  // Stanja
  const { t } = useTranslation();
  const { classId } = useParams();
  const [students, setStudents] = useState([]);
  const [connectedStudents, setConnectedStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [filteredConnectedStudents, setFilteredConnectedStudents] = useState(
    []
  );
  const [works, setWorks] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [currentWorkIndex, setCurrentWorkIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [tabIndex, setTabIndex] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [originalStudents, setOriginalStudents] = useState([]); // Novo stanje za originalni redosled učenika

  const [openAddDialog, setOpenAddDialog] = useState(false);

  const itemRefs = useRef({});

  let isNextButtonDisabled = false;
  const navigate = useNavigate();

  // Dovlačenje skeniranih nepovezanih radova
  const fetchWorks = () => {
    fetch(
      `${API_BASE_URL}/api/finalConnectingWork.php?action=getWorksByClass&classId=${classId}`
    )
      .then((response) => response.json())
      .then((data) => {
        // Filtriraj radove koji nemaju 'connected' status
        const filteredWorks = data.filter((work) => !work.connected);
        setWorks(filteredWorks);
      })
      .catch((error) => console.error('Error fetching works:', error));
  };

  const fetchStudents = () => {
    fetch(`${API_BASE_URL}/api/finalStudents.php?StudentsASCByClassId=${classId}`)
      .then((response) => response.json())
      .then((data) => {
        setOriginalStudents(data);
        const unconnected = data.filter(
          (student) => student.povezanRad === '0'
        );
        const connected = data.filter((student) => student.povezanRad === '1');
        setStudents(unconnected);
        setConnectedStudents(connected);
        setFilteredStudents(unconnected);
        setFilteredConnectedStudents(connected);
      })
      .catch((error) => console.error('Error fetching students:', error));
  };

  useEffect(() => {
    fetchStudents();
    fetchWorks();
  }, [classId]);

  // Kretanje kroz učenike pomoću strelica
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'ArrowDown') {
        // Selektovanje sledećeg učenika
        event.preventDefault();
        if (tabIndex === 0 && filteredStudents.length > 0) {
          const currentIndex = filteredStudents.findIndex(
            (student) => student.id === selectedStudent
          );
          const nextIndex = Math.min(
            currentIndex + 1,
            filteredStudents.length - 1
          );
          setSelectedStudent(filteredStudents[nextIndex].id);
        } else if (tabIndex === 1 && filteredConnectedStudents.length > 0) {
          const currentIndex = filteredConnectedStudents.findIndex(
            (student) => student.id === selectedStudent
          );
          const nextIndex = Math.min(
            currentIndex + 1,
            filteredConnectedStudents.length - 1
          );
          setSelectedStudent(filteredConnectedStudents[nextIndex].id);
        }
      } else if (event.key === 'ArrowUp') {
        // Selektovanje prethodnog učenika
        event.preventDefault();
        if (tabIndex === 0 && filteredStudents.length > 0) {
          const currentIndex = filteredStudents.findIndex(
            (student) => student.id === selectedStudent
          );
          const prevIndex = Math.max(currentIndex - 1, 0);
          setSelectedStudent(filteredStudents[prevIndex].id);
        } else if (tabIndex === 1 && filteredConnectedStudents.length > 0) {
          const currentIndex = filteredConnectedStudents.findIndex(
            (student) => student.id === selectedStudent
          );
          const prevIndex = Math.max(currentIndex - 1, 0);
          setSelectedStudent(filteredConnectedStudents[prevIndex].id);
        }
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        // Promena taba sa levo/desno strelicom
        event.preventDefault();
        const newTabIndex =
          event.key === 'ArrowLeft'
            ? Math.max(tabIndex - 1, 0)
            : Math.min(tabIndex + 1, 1);
        setTabIndex(newTabIndex);
      } else if (event.key === 'Enter') {
        // Povezivanje učenika sa radom kada se pritisne Enter
        event.preventDefault();
        if (tabIndex === 0 && selectedStudent) {
          handleConnectClick();
        } else if (tabIndex === 1 && selectedStudent) {
          handleOpenDialog();
        }
      }
    };

    // Dodaj event listener za tastaturu kada se komponenta montira
    window.addEventListener('keydown', handleKeyDown);

    // Ukloni event listener kada se komponenta demontira
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedStudent, filteredStudents, filteredConnectedStudents, tabIndex]);

  // Pretraga
  useEffect(() => {
    const normalizeText = (text) => {
      // Konvertujemo u mala slova
      let result = text.toLowerCase();
      // Transliterujemo ćirilicu u latinicu
      result = cyrillicToLatin(result);
      return result;
    };

    const normalizedSearchTerm = normalizeText(searchTerm);

    setFilteredStudents(
      students.filter((student) => {
        const normalizedStudentName = normalizeText(student.name);
        return normalizedStudentName.includes(normalizedSearchTerm);
      })
    );

    setFilteredConnectedStudents(
      connectedStudents.filter((student) => {
        const normalizedStudentName = normalizeText(student.name);
        return normalizedStudentName.includes(normalizedSearchTerm);
      })
    );
  }, [searchTerm, students, connectedStudents]);

  useEffect(() => {
    if (selectedStudent && itemRefs.current[selectedStudent]) {
      itemRefs.current[selectedStudent].scrollIntoView({
        block: 'nearest',
        behavior: 'smooth'
      });
    }
  }, [selectedStudent]);

  // Navigacija nazad
  const handleBackClick = () => {
    navigate('final-review-schools');
  };

  // Povezivanje radova sa učenikom
  // POVEZIVANJE UČENIKA
  const handleConnectClick = () => {
    const currentWork = works[currentWorkIndex];

    console.log('Povezivanje učenika:', selectedStudent);
    console.log('current work index:', currentWorkIndex);
    console.log('works:', works);
    console.log('Sa radom:', currentWork.fileName);

    if (!selectedStudent || !currentWork) return;

    axios
      .post(`${API_BASE_URL}/api/finalConnectingWork.php`, {
        action: 'connectWork',
        classId,
        studentId: selectedStudent,
        fileName: currentWork.fileName
      })
      .then((response) => {
        const data = response.data;
        if (data.status === 'success') {
          // 1) Pronađi učenika (iz nepovezane liste)
          const connectedStudent = students.find(
            (student) => student.id === selectedStudent
          );

          // 2) Napravi ažuriranog učenika
          const updatedStudent = {
            ...connectedStudent,
            connected: true,
            povezanRad: '1',
            fileName: currentWork.fileName
          };

          // 3) Dodaj tog učenika u listu povezanih
          setConnectedStudents([...connectedStudents, updatedStudent]);

          // 4) Pronađi indeks u filteredStudents
          const currentIndex = filteredStudents.findIndex(
            (s) => s.id === selectedStudent
          );

          // 5) Ukloni ga iz students i filteredStudents
          const remainingStudents = students.filter(
            (student) => student.id !== selectedStudent
          );
          setStudents(remainingStudents);

          const updatedFiltered = filteredStudents.filter(
            (student) => student.id !== selectedStudent
          );
          setFilteredStudents(updatedFiltered);

          // 6) Selektuj „sledećeg“ ili poslednjeg u novoj listi
          if (updatedFiltered.length > 0) {
            if (currentIndex >= updatedFiltered.length) {
              // Bili smo na poslednjem, sada selektuj novog poslednjeg
              setSelectedStudent(
                updatedFiltered[updatedFiltered.length - 1].id
              );
            } else {
              // Inače selektuj „istu poziciju“ (sledeći u nizu)
              setSelectedStudent(updatedFiltered[currentIndex].id);
            }
          } else {
            // Nema više učenika
            setSelectedStudent(null);
          }

          // 7) Ukloni rad iz works
          const updatedWorks = works.filter(
            (work) => work.fileName !== currentWork.fileName
          );
          setWorks(updatedWorks);

          // Ako nema više radova, resetuj currentWorkIndex
          if (currentWorkIndex >= updatedWorks.length) {
            setCurrentWorkIndex(0);
          }

          setSearchTerm('');

          // 8) Prikaži notifikaciju
          setSnackbarMessage('review.success_connecting');
          setSnackbarSeverity('success');
          setSnackbarOpen(true);
        } else {
          console.error('Povezivanje nije uspelo:', data.message);
          setSnackbarMessage('error_connecting');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
        }
      })
      .catch((error) => {
        console.error('Error connecting work:', error);
        setSnackbarMessage('review.error_occurred');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      });
  };

  // Prebacivanje na sledeći rad
  const handleNextWork = () => {
    if (isNextButtonDisabled) return;
    isNextButtonDisabled = true;

    setCurrentWorkIndex((prevIndex) =>
      Math.min(prevIndex + 1, works.length - 1)
    );

    // Oslobađamo dugme nakon 300ms (možeš prilagoditi ovo vreme)
    setTimeout(() => {
      isNextButtonDisabled = false;
    }, 300);
  };

  const handleAddStudentSuccess = () => {
    setOpenAddDialog(false);
    fetchStudents(); // ponovo preuzmi listu učenika
  };

  // Prebacivanje na prethodni rad
  const handlePreviousWork = () => {
    setCurrentWorkIndex((prevIndex) => Math.max(prevIndex - 1, 0));
  };

  // Ponovno povezivanje rada i učenika (odvezivanje)
  const handleDisconnectClick = () => {
    const currentStudent = connectedStudents.find(
      (student) => student.id === selectedStudent
    );
    if (!currentStudent || !currentStudent.fileName) {
      console.error('File name is not available for the selected student.');
      setSnackbarMessage('File name is not available.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    const fileName = currentStudent.fileName.trim();
    if (!fileName) {
      console.error('File name is not valid.');
      setSnackbarMessage('File name is not valid.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    fetch(`${API_BASE_URL}/api/finalConnectingWork.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        action: 'disconnectWork',
        classId,
        studentId: selectedStudent,
        fileName
      })
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status === 'success') {
          const updatedConnectedStudents = connectedStudents.filter(
            (student) => student.id !== selectedStudent
          );
          setConnectedStudents(updatedConnectedStudents);

          const updatedStudent = {
            ...currentStudent,
            connected: false,
            povezanRad: '0',
            fileName: null
          };

          const currentIndex = filteredConnectedStudents.findIndex(
            (s) => s.id === selectedStudent
          );
          const newFilteredConnected = filteredConnectedStudents.filter(
            (s) => s.id !== selectedStudent
          );
          setFilteredConnectedStudents(newFilteredConnected);

          const originalIndex = originalStudents.findIndex(
            (s) => s.id === updatedStudent.id
          );

          if (originalIndex === -1) {
            // Ako ga nema u originalStudents, samo dodaj na kraj
            setStudents([...students, updatedStudent]);
            setFilteredStudents([...filteredStudents, updatedStudent]);
          } else {
            // Ubacivanje nazad na odgovarajući indeks prema originalStudents
            const newStudents = [...students];
            let insertPos = 0;
            for (let i = 0; i < newStudents.length; i++) {
              const idxInOriginal = originalStudents.findIndex(
                (s) => s.id === newStudents[i].id
              );
              if (idxInOriginal > originalIndex) {
                break;
              }
              insertPos++;
            }
            newStudents.splice(insertPos, 0, updatedStudent);
            setStudents(newStudents);

            const newFiltered = [...filteredStudents];
            insertPos = 0;
            for (let i = 0; i < newFiltered.length; i++) {
              const idxInOriginal = originalStudents.findIndex(
                (s) => s.id === newFiltered[i].id
              );
              if (idxInOriginal > originalIndex) {
                break;
              }
              insertPos++;
            }
            newFiltered.splice(insertPos, 0, updatedStudent);
            setFilteredStudents(newFiltered);
          }

          if (newFilteredConnected.length > 0) {
            if (currentIndex >= newFilteredConnected.length) {
              setSelectedStudent(
                newFilteredConnected[newFilteredConnected.length - 1].id
              );
            } else {
              setSelectedStudent(newFilteredConnected[currentIndex].id);
            }
          } else {
            setSelectedStudent(null);
          }

          setWorks([...works, { fileName, connected: false }]);
          setOpenDialog(false);
          setSnackbarMessage('review.success_disconnecting');
          setSnackbarSeverity('success');
          setSnackbarOpen(true);
        } else {
          console.error('Disconnect failed:', data.message);
          setSnackbarMessage('review.error_disconnecting');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
        }
      })
      .catch((error) => {
        console.error('Error disconnecting work:', error);
        setSnackbarMessage('review.error_occurred');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      });
  };

  // Promena taba
  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
    if (newValue === 0 && filteredStudents.length > 0) {
      setSelectedStudent(filteredStudents[0].id);
    } else if (newValue === 1 && filteredConnectedStudents.length > 0) {
      setSelectedStudent(filteredConnectedStudents[0].id);
    }
  };

  // Otvaranje dialoga
  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const currentWork = works[currentWorkIndex];
  const connectedStudentWork = selectedStudent
    ? connectedStudents.find((student) => student.id === selectedStudent)
    : null;

  return (    
    <div
      style={{
        padding: '20px',
        backgroundColor: '#f5f5f5',
        minHeight: '100vh',
        minWidth: '100vh'
      }}
    >
      <Button
        variant="contained"
        onClick={handleBackClick}
        aria-hidden={false}
        style={{
          marginBottom: '10px',
          backgroundColor: '#0003',
          color: '#fff',
          borderRadius: '20%',
          minWidth: '40px',
          minHeight: '40px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
          cursor: 'pointer'
        }}
      >
        <ArrowBackIcon />
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={() => setOpenAddDialog(true)}
        style={{
          marginBottom: '10px',
          backgroundColor: '#50C878',
          color: '#FFFFFF',
          borderRadius: '20%',
          minWidth: '40px',
          minHeight: '40px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          cursor: 'pointer'
        }}
      >
        {/* {{ t('meni.addNewStudent')}} */}
        Додај ученика
      </Button>

      <Tabs
        value={tabIndex}
        onChange={handleTabChange}
        style={{
          padding: '10px',
          borderRadius: '12px'
        }}
      >
        <Tab
          label={t('review.unconnected_students')}
          style={{
            color: tabIndex === 0 ? '#fff' : '#1976D2',
            backgroundColor: tabIndex === 0 ? '#1976D2' : '#fff',
            borderRadius: '8px 8px 0 0',
            fontWeight: 'bold',
            padding: '10px',
            marginRight: '5px'
          }}
        />
        <Tab
          label={t('review.connected_students')}
          style={{
            color: tabIndex === 1 ? '#fff' : '#1976D2',
            backgroundColor: tabIndex === 1 ? '#1976D2' : '#fff',
            borderRadius: '8px 8px 0 0',
            fontWeight: 'bold',
            padding: '10px'
          }}
        />
      </Tabs>

      <Grid container spacing={2}>
        {/* Prikaz nepovezanih učenika */}
        {tabIndex === 0 && (
          <div
            style={{
              backgroundColor: '#f5f5f5',
              minHeight: '100vh',
              marginTop: '0px',
              paddingTop: '0px'
            }}
          >
            <Grid container spacing={2} style={{ marginTop: '5px' }}>
              <Grid item xs={4} style={{ flexGrow: 1 }}>
                <div
                  style={{
                    backgroundColor: '#e3f2fd',
                    padding: '20px',
                    borderRadius: '12px',
                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <TextField
                    label={t('review.search_students')}
                    variant="outlined"
                    fullWidth
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      borderRadius: '8px',
                      backgroundColor: '#ffffff',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                      marginBottom: '20px'
                    }}
                    InputProps={{
                      style: {
                        fontSize: '16px',
                        color: '#000000'
                      }
                    }}
                    InputLabelProps={{
                      style: { color: '#000000de' }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: '#cccccc'
                        },
                        '&:hover fieldset': {
                          borderColor: '#2596be'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#2596be'
                        }
                      }
                    }}
                  />

                  <div
                    style={{
                      maxHeight: '60vh',
                      overflowY: 'auto',
                      backgroundColor: '#fff',
                      padding: '10px',
                      borderRadius: '8px',
                      boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                      fontSize: '14px'
                    }}
                  >
                    <List>
                      {filteredStudents.map((student) => (
                        <ListItem
                          ref={(el) => { itemRefs.current[student.id] = el; }}
                          key={student.id}
                          button
                          selected={selectedStudent === student.id}
                          onClick={() => setSelectedStudent(student.id)}
                          onDoubleClick={() => {
                            setSelectedStudent(student.id);
                            handleConnectClick();
                          }}
                          style={{
                            backgroundColor:
                              selectedStudent === student.id
                                ? '#1976D2'
                                : '#f0f0f0',
                            color:
                              selectedStudent === student.id ? '#fff' : '#000',
                            borderRadius: '8px',
                            margin: '5px 0',
                            paddingTop: '5px',
                            transition: 'background-color 0.3s ease',
                            boxShadow:
                              selectedStudent === student.id
                                ? '0px 4px 8px rgba(0, 0, 0, 0.1)'
                                : 'none'
                          }}
                        >
                          {student.name} ({t('review.class')}: {student.class})
                        </ListItem>
                      ))}
                    </List>
                  </div>

                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    aria-hidden={false}
                    onClick={handleConnectClick}
                    disabled={!selectedStudent}
                    style={{
                      backgroundColor: selectedStudent ? '#1976D2' : '#e0e0e0',
                      color: '#fff',
                      borderRadius: '8px',
                      fontWeight: 'bold',
                      padding: '10px 20px',
                      transition: 'background-color 0.3s ease'
                    }}
                  >
                    {t('review.connect')}
                  </Button>
                </div>
              </Grid>

              <Grid
                item
                xs={8}
                style={{
                  textAlign: 'center',
                  minHeight: '500px',
                  flexGrow: '1',
                  width: '900px'
                }}
              >
                {currentWork ? (
                  <div
                    style={{
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      marginLeft: '50px',
                      marginTop: '-25px',
                      alignItems: 'center'
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}
                    >
                      <Button
                        variant="outlined"
                        onClick={handlePreviousWork}
                        aria-hidden={false}
                        disabled={currentWorkIndex === 0}
                        style={{
                          backgroundColor:
                            currentWorkIndex === 0 ? '#cfcfcf' : '#1976D2',
                          color: currentWorkIndex === 0 ? '#666666' : '#fff',
                          fontWeight: 'bold',
                          boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
                          borderRadius: '8px',
                          fontSize: '10px',
                          padding: '6px 10px',
                          widows: '20px',
                          marginRight: '20px',
                          cursor:
                            currentWorkIndex === 0 ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {t('review.previous')}
                      </Button>
                      {tabIndex === 0 && currentWork && (
                        <img
                          src={`${API_BASE_URL}/api/radovi/finalni_radovi/${classId}/${currentWork.fileName}.jpg`}
                          alt={t('review.work')}
                          style={{
                            maxWidth: '90%',
                            marginTop: '-50px',
                            height: 'auto',
                            borderRadius: '8px',
                            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)'
                          }}
                        />
                      )}

                      <Button
                        variant="outlined"
                        onClick={handleNextWork}
                        aria-hidden={false}
                        disabled={currentWorkIndex === works.length - 1}
                        style={{
                          backgroundColor:
                            currentWorkIndex === works.length - 1
                              ? '#cfcfcf'
                              : '#1976D2',
                          color:
                            currentWorkIndex === works.length - 1
                              ? '#666666'
                              : '#fff',
                          fontWeight: 'bold',
                          boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
                          borderRadius: '8px',
                          fontSize: '10px',
                          padding: '6px 10px',
                          width: '30px',
                          marginLeft: '20px',
                          cursor:
                            currentWorkIndex === works.length - 1
                              ? 'not-allowed'
                              : 'pointer'
                        }}
                      >
                        {t('review.next')}
                      </Button>
                    </div>
                    <Typography variant="h6" style={{ marginBottom: '10px' }}>
                      {t('review.work_number', {
                        current: currentWorkIndex + 1,
                        total: works.length
                      })}
                    </Typography>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', marginTop: '30px' }}>
                    <Typography
                      variant="h6"
                      style={{
                        color: '#999999',
                        marginBottom: '20px'
                      }}
                    >
                      Нема више радова за повезивање
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        window.location.href = `/kengur2/#/final-review-schools-students?classId=${classId}`;
                      }}
                      sx={{
                        backgroundColor: '#4cceac',
                        color: '#fff',
                        fontWeight: 'bold',
                        fontSize: '16px',
                        padding: '10px 20px',
                        borderRadius: '8px',
                        textTransform: 'none',
                        boxShadow: '0px 4px 8px rgba(0,0,0,0.2)',
                        '&:hover': {
                          backgroundColor: '#43b59e'
                        }
                      }}
                    >
                      Иди на прегледање
                    </Button>
                  </div>
                )}
              </Grid>
            </Grid>
          </div>
        )}

        {/* Prikaz povezanih učenika */}
        {tabIndex === 1 && (
          <>
            <Grid item xs={3}>
              <div
                style={{
                  backgroundColor: '#e3f2fd',
                  padding: '20px',
                  borderRadius: '12px',
                  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)'
                }}
              >
                <TextField
                  label={t('review.search_students')}
                  variant="outlined"
                  fullWidth
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    borderRadius: '8px',
                    backgroundColor: '#ffffff',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    marginBottom: '20px'
                  }}
                />
                <div
                  style={{
                    maxHeight: '60vh',
                    overflowY: 'auto',
                    backgroundColor: '#fff',
                    padding: '10px',
                    borderRadius: '8px',
                    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <List>
                    {filteredConnectedStudents.map((student) => (
                      <ListItem
                        ref={(el) => { itemRefs.current[student.id] = el; }}
                        key={student.id}
                        button
                        selected={selectedStudent === student.id}
                        onClick={() => setSelectedStudent(student.id)}
                        onDoubleClick={() => {
                          setSelectedStudent(student.id);
                          handleOpenDialog();
                        }}
                        style={{
                          backgroundColor:
                            selectedStudent === student.id
                              ? '#1976D2'
                              : '#f0f0f0',
                          color:
                            selectedStudent === student.id ? '#fff' : '#000',
                          borderRadius: '8px',
                          margin: '5px 0',
                          transition: 'background-color 0.3s ease',
                          boxShadow:
                            selectedStudent === student.id
                              ? '0px 4px 8px rgba(0, 0, 0, 0.1)'
                              : 'none'
                        }}
                      >
                        {student.name} ({t('review.class')}: {student.class})
                      </ListItem>
                    ))}
                  </List>
                </div>
                <Button
                  variant="contained"
                  color="secondary"
                  fullWidth
                  aria-hidden={false}
                  onClick={handleOpenDialog}
                  disabled={!selectedStudent}
                  style={{
                    backgroundColor: '#d32f2f',
                    color: '#fff',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    height: '50px',
                    borderRadius: '8px',
                    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
                    marginTop: '20px'
                  }}
                >
                  {t('review.disconnect')}
                </Button>
              </div>
            </Grid>

            <Grid item xs={6} style={{ textAlign: 'center' }}>
              {tabIndex === 1 && (
                <>
                  {connectedStudentWork ? (
                    <img
                      src={`${API_BASE_URL}/api/radovi/finalni_radovi/${classId}/${connectedStudentWork.fileName}_connected_${connectedStudentWork.id}.jpg`}
                      alt={t('review.work')}
                      style={{
                        maxWidth: '90%',
                        height: 'auto',
                        marginLeft: '40px',
                        marginTop: '-50px',
                        borderRadius: '8px',
                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)'
                      }}
                    />
                  ) : (
                    <Typography variant="h6">
                      {t('review.no_work_for_display')}
                    </Typography>
                  )}
                </>
              )}
            </Grid>
          </>
        )}
      </Grid>

      {works.length === 0 && (
        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              window.location.href = `/kengur2/#/final-review-schools-students?classId=${classId}`;
            }}
            sx={{
              backgroundColor: '#4cceac',
              color: '#fff',
              fontWeight: 'bold',
              fontSize: '16px',
              padding: '10px 20px',
              borderRadius: '8px',
              textTransform: 'none',
              boxShadow: '0px 4px 8px rgba(0,0,0,0.2)',
              '&:hover': {
                backgroundColor: '#43b59e'
              }
            }}
          >
            {t('review.go_to_review')}
          </Button>
        </div>
      )}

      <AddStudentDialog
        open={openAddDialog}
        handleClose={() => setOpenAddDialog(false)}
        schoolId={classId}
        // Možeš proslediti i allSchoolView={true} ako želiš
        onSuccess={handleAddStudentSuccess} // callback kad se uspešno doda
      />

      {/* Dialog za potvrdu odvezivanja */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            handleDisconnectClick();
          }
        }}
      >
        <DialogTitle id="alert-dialog-title">
          {t('review.disconnect_confirmation')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t('review.disconnect_confirmation_message')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            {t('review.cancel')}
          </Button>
          <Button onClick={handleDisconnectClick} color="secondary" autoFocus>
            {t('review.disconnect')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar za prikaz poruka */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {t(`${snackbarMessage}`)}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default FinalConnectWorkPage;
